"use client";

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSession } from "next-auth/react";
import {
  Search,
  Calendar,
  MapPin,
  Tag,
  ChevronLeft,
  ChevronRight,
  X,
  ArrowLeft,
  Loader2,
  AlertCircle,
  RefreshCw,
  Images,
  Star,
  Users,
  Camera,
  Filter,
  Grid3X3,
  LayoutGrid,
  Heart,
  Share2,
  Download,
  ZoomIn,
  Sparkles,
} from "lucide-react";
import api from "@/lib/axiosInstance";
import Link from "next/link";

interface Chapter {
  _id: string;
  chapterName: string;
}

interface Album {
  _id: string;
  title: string;
  images: string[];
  description: string;
  date: string;
  tags: string[];
  category: string;
  chapter: Chapter;
  likes: number;
  comments: number;
  featured: boolean;
  createdAt: string;
  updatedAt: string;
}

// Album categories for filtering
const ALBUM_CATEGORIES = [
  { value: "all", label: "All Categories", icon: Grid3X3 },
  { value: "fellowship", label: "Fellowship", icon: Users },
  { value: "outreach", label: "Outreach", icon: Heart },
  { value: "worship", label: "Worship", icon: Sparkles },
  { value: "conference", label: "Conference", icon: Calendar },
  { value: "community", label: "Community", icon: Users },
  { value: "youth", label: "Youth", icon: Star },
  { value: "missions", label: "Missions", icon: MapPin },
  { value: "other", label: "Other", icon: Tag },
];

// Custom hook for fetching albums
const useAlbums = (chapterId?: string) => {
  return useQuery({
    queryKey: ["albums", chapterId],
    queryFn: async () => {
      let endpoint = "/gallery/albums";
      if (chapterId) {
        endpoint = `/chapters/${chapterId}/albums`;
      }
      const response = await api.get(endpoint);
      return response.data as Album[];
    },
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
    refetchOnWindowFocus: false,
    retry: 2,
  });
};

// Single album hook for detail view
const useAlbum = (albumId: string | null) => {
  return useQuery({
    queryKey: ["album", albumId],
    queryFn: async () => {
      if (!albumId) throw new Error("No album ID provided");
      const response = await api.get(`/gallery/albums/${albumId}`);
      return response.data as Album;
    },
    enabled: !!albumId,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
    refetchOnWindowFocus: false,
    retry: 2,
  });
};

const GalleryPage = () => {
  const { data: session } = useSession();
  const [currentView, setCurrentView] = useState<"gallery" | "album">("gallery");
  const [selectedAlbumId, setSelectedAlbumId] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState<"grid" | "masonry">("grid");

  // Gallery filters
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedChapter, setSelectedChapter] = useState("all");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedYear, setSelectedYear] = useState("all");
  const [showUserChapterOnly, setShowUserChapterOnly] = useState(false);

  // Determine which albums to fetch
  const fetchChapterId = useMemo(() => {
    if (showUserChapterOnly && session?.user?.chapter) {
      return session.user.chapter;
    }
    return undefined;
  }, [showUserChapterOnly, session?.user?.chapter]);

  // Fetch albums
  const {
    data: albums = [],
    isLoading: albumsLoading,
    isError: albumsError,
    error: albumsErrorData,
    refetch: refetchAlbums,
    isRefetching: albumsRefetching,
  } = useAlbums(fetchChapterId);

  // Fetch single album for detail view
  const {
    data: selectedAlbum,
    isLoading: albumLoading,
    isError: albumError,
  } = useAlbum(selectedAlbumId);

  // Get unique chapters, categories, and years from fetched data
  const { chapters, years } = useMemo(() => {
    const uniqueChapters = [
      "all",
      ...new Set(albums.map((album) => album.chapter?.chapterName).filter(Boolean)),
    ];
    const uniqueYears = [
      "all",
      ...new Set(
        albums.map((album) => new Date(album.date).getFullYear().toString())
      ),
    ];
    return {
      chapters: uniqueChapters,
      years: uniqueYears.sort((a, b) => (b === "all" ? -1 : parseInt(b) - parseInt(a))),
    };
  }, [albums]);

  // Filter albums
  const filteredAlbums = useMemo(() => {
    let result = [...albums];

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter(
        (album) =>
          album.title.toLowerCase().includes(searchLower) ||
          album.description.toLowerCase().includes(searchLower) ||
          album.tags.some((tag) => tag.toLowerCase().includes(searchLower))
      );
    }

    if (selectedChapter !== "all") {
      result = result.filter(
        (album) => album.chapter?.chapterName === selectedChapter
      );
    }

    if (selectedCategory !== "all") {
      result = result.filter((album) => album.category === selectedCategory);
    }

    if (selectedYear !== "all") {
      result = result.filter(
        (album) =>
          new Date(album.date).getFullYear().toString() === selectedYear
      );
    }

    result.sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
    );

    return result;
  }, [albums, searchTerm, selectedChapter, selectedCategory, selectedYear]);

  // Featured albums
  const featuredAlbums = useMemo(() => {
    return albums.filter((album) => album.featured).slice(0, 3);
  }, [albums]);

  const openAlbum = useCallback((album: Album) => {
    setSelectedAlbumId(album._id);
    setCurrentView("album");
  }, []);

  const openImageViewer = useCallback((album: Album, imageIndex: number) => {
    setCurrentImageIndex(imageIndex);
    setSelectedImage(album.images[imageIndex]);
  }, []);

  const closeImageViewer = useCallback(() => {
    setSelectedImage(null);
  }, []);

  const navigateImage = useCallback((direction: "prev" | "next") => {
    if (!selectedAlbum) return;

    let newIndex;
    if (direction === "prev") {
      newIndex =
        currentImageIndex === 0
          ? selectedAlbum.images.length - 1
          : currentImageIndex - 1;
    } else {
      newIndex =
        currentImageIndex === selectedAlbum.images.length - 1
          ? 0
          : currentImageIndex + 1;
    }

    setCurrentImageIndex(newIndex);
    setSelectedImage(selectedAlbum.images[newIndex]);
  }, [selectedAlbum, currentImageIndex]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (selectedImage) {
        if (e.key === "Escape") closeImageViewer();
        if (e.key === "ArrowLeft") navigateImage("prev");
        if (e.key === "ArrowRight") navigateImage("next");
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedImage, navigateImage, closeImageViewer]);

  const clearFilters = useCallback(() => {
    setSearchTerm("");
    setSelectedChapter("all");
    setSelectedCategory("all");
    setSelectedYear("all");
    setShowUserChapterOnly(false);
  }, []);

  const hasActiveFilters = searchTerm || selectedChapter !== "all" || selectedCategory !== "all" || selectedYear !== "all" || showUserChapterOnly;

  // Statistics
  const stats = useMemo(() => ({
    totalAlbums: albums.length,
    totalPhotos: albums.reduce((acc, album) => acc + album.images.length, 0),
    totalChapters: chapters.length - 1,
    featuredCount: albums.filter((a) => a.featured).length,
  }), [albums, chapters]);

  // Album Card Component
  const AlbumCard = ({ album, index }: { album: Album; index: number }) => {
    const coverImage = album.images[0];
    const previewImages = album.images.slice(1, 4);

    return (
      <div
        className="group relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 cursor-pointer transform hover:-translate-y-2"
        onClick={() => openAlbum(album)}
        style={{ animationDelay: `${index * 50}ms` }}
      >
        {/* Main Image */}
        <div className="relative h-64 overflow-hidden">
          <img
            src={coverImage}
            alt={album.title}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
          />
          
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-primary-900/90 via-primary-900/30 to-transparent" />
          
          {/* Featured Badge */}
          {album.featured && (
            <div className="absolute top-4 left-4 flex items-center gap-1.5 px-3 py-1.5 bg-secondary-500 text-white rounded-full text-xs font-semibold shadow-lg">
              <Star className="w-3 h-3 fill-current" />
              Featured
            </div>
          )}
          
          {/* Image Count */}
          <div className="absolute top-4 right-4 flex items-center gap-1.5 px-3 py-1.5 bg-black/50 backdrop-blur-sm text-white rounded-full text-xs font-medium">
            <Images className="w-3 h-3" />
            {album.images.length} photos
          </div>

          {/* Preview Thumbnails */}
          {previewImages.length > 0 && (
            <div className="absolute bottom-4 right-4 flex gap-1">
              {previewImages.map((img, idx) => (
                <div
                  key={idx}
                  className="w-10 h-10 rounded-lg overflow-hidden border-2 border-white/50 shadow-md opacity-0 group-hover:opacity-100 transition-all duration-300"
                  style={{ transitionDelay: `${idx * 100}ms` }}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </div>
              ))}
              {album.images.length > 4 && (
                <div className="w-10 h-10 rounded-lg bg-primary-600/80 backdrop-blur-sm border-2 border-white/50 flex items-center justify-center text-white text-xs font-bold opacity-0 group-hover:opacity-100 transition-all duration-300" style={{ transitionDelay: "300ms" }}>
                  +{album.images.length - 4}
                </div>
              )}
            </div>
          )}

          {/* Title Overlay */}
          <div className="absolute bottom-0 left-0 right-0 p-5">
            <div className="flex items-center gap-2 mb-2">
              <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-white/20 backdrop-blur-sm text-white">
                {album.chapter?.chapterName || "CONCES"}
              </span>
              <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-secondary-500/80 text-white capitalize">
                {album.category}
              </span>
            </div>
            <h3 className="text-xl font-bold text-white mb-1 line-clamp-1">
              {album.title}
            </h3>
            <p className="text-white/70 text-sm line-clamp-2">
              {album.description}
            </p>
          </div>
        </div>

        {/* Card Footer */}
        <div className="p-4 bg-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-gray-500 text-sm">
              <Calendar className="w-4 h-4" />
              {new Date(album.date).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
              })}
            </div>
            <div className="flex items-center gap-3">
              {album.tags.slice(0, 2).map((tag, idx) => (
                <span key={idx} className="text-xs text-primary-600 font-medium">
                  #{tag}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Hover Action */}
        <div className="absolute inset-0 flex items-center justify-center bg-primary-900/60 opacity-0 group-hover:opacity-100 transition-all duration-300">
          <div className="flex items-center gap-2 px-6 py-3 bg-white text-primary-700 rounded-full font-semibold shadow-xl transform scale-90 group-hover:scale-100 transition-transform duration-300">
            <Camera className="w-5 h-5" />
            View Album
          </div>
        </div>
      </div>
    );
  };

  // Loading Skeleton
  const AlbumSkeleton = () => (
    <div className="bg-white rounded-2xl overflow-hidden shadow-lg animate-pulse">
      <div className="h-64 bg-gradient-to-br from-gray-200 to-gray-300" />
      <div className="p-4 space-y-3">
        <div className="h-4 bg-gray-200 rounded w-3/4" />
        <div className="h-3 bg-gray-200 rounded w-1/2" />
      </div>
    </div>
  );

  // Gallery View
  const GalleryView = () => (
    <div className="min-h-screen bg-gradient-to-b from-accent-100 to-white">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-primary-800 via-primary-700 to-primary-900 py-20 lg:py-28">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>
        
        {/* Animated Orbs */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-secondary-500/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-10 right-20 w-96 h-96 bg-primary-400/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: "1s" }} />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white/90 text-sm font-medium mb-6">
              <Camera className="w-4 h-4" />
              <span>Memories That Matter</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Our <span className="text-secondary-400">Gallery</span>
            </h1>
            <p className="text-xl text-white/80 max-w-2xl mx-auto mb-10">
              Explore moments of fellowship, growth, and celebration from our community chapters across the nation.
            </p>

            {/* Search Bar */}
            <div className="max-w-2xl mx-auto relative">
              <div className="flex items-center bg-white rounded-2xl shadow-2xl overflow-hidden">
                <div className="flex-1 flex items-center px-6 py-4">
                  <Search className="w-5 h-5 text-gray-400 mr-3" />
                  <input
                    type="text"
                    placeholder="Search albums, events, or tags..."
                    className="w-full text-gray-700 placeholder-gray-400 focus:outline-none text-lg"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`flex items-center gap-2 px-6 py-4 font-medium transition-colors ${
                    showFilters || hasActiveFilters
                      ? "bg-primary-600 text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  <Filter className="w-5 h-5" />
                  Filters
                  {hasActiveFilters && (
                    <span className="w-2 h-2 bg-secondary-500 rounded-full" />
                  )}
                </button>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="flex flex-wrap justify-center gap-8 mt-12">
              {[
                { icon: Images, value: stats.totalAlbums, label: "Albums" },
                { icon: Camera, value: stats.totalPhotos, label: "Photos" },
                { icon: MapPin, value: stats.totalChapters, label: "Chapters" },
                { icon: Star, value: stats.featuredCount, label: "Featured" },
              ].map((stat, idx) => (
                <div key={idx} className="flex items-center gap-3 text-white">
                  <div className="w-12 h-12 rounded-xl bg-white/10 backdrop-blur-sm flex items-center justify-center">
                    <stat.icon className="w-6 h-6" />
                  </div>
                  <div className="text-left">
                    <div className="text-2xl font-bold">{stat.value}</div>
                    <div className="text-white/70 text-sm">{stat.label}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Filters Panel */}
      {showFilters && (
        <section className="bg-white border-b border-gray-200 shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Chapter Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <MapPin className="w-4 h-4 inline mr-1" />
                  Chapter
                </label>
                <select
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-gray-50 text-gray-700"
                  value={selectedChapter}
                  onChange={(e) => setSelectedChapter(e.target.value)}
                >
                  <option value="all">All Chapters</option>
                  {chapters.slice(1).map((chapter) => (
                    <option key={chapter} value={chapter}>
                      {chapter}
                    </option>
                  ))}
                </select>
              </div>

              {/* Category Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Tag className="w-4 h-4 inline mr-1" />
                  Category
                </label>
                <select
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-gray-50 text-gray-700"
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                >
                  {ALBUM_CATEGORIES.map((category) => (
                    <option key={category.value} value={category.value}>
                      {category.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Year Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Calendar className="w-4 h-4 inline mr-1" />
                  Year
                </label>
                <select
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-gray-50 text-gray-700"
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                >
                  <option value="all">All Years</option>
                  {years.slice(1).map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>

              {/* User Chapter Toggle */}
              <div className="flex items-end">
                {session?.user?.chapter ? (
                  <label className="flex items-center gap-3 px-4 py-3 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 transition-colors w-full">
                    <input
                      type="checkbox"
                      checked={showUserChapterOnly}
                      onChange={(e) => setShowUserChapterOnly(e.target.checked)}
                      className="w-5 h-5 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                    <span className="text-gray-700 font-medium">My Chapter Only</span>
                  </label>
                ) : (
                  <button
                    onClick={() => refetchAlbums()}
                    disabled={albumsRefetching}
                    className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors disabled:opacity-50"
                  >
                    <RefreshCw className={`w-5 h-5 ${albumsRefetching ? "animate-spin" : ""}`} />
                    Refresh
                  </button>
                )}
              </div>
            </div>

            {/* Active Filters & Clear */}
            {hasActiveFilters && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm text-gray-500">Active filters:</span>
                  {searchTerm && (
                    <span className="px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-sm">
                      Search: "{searchTerm}"
                    </span>
                  )}
                  {selectedChapter !== "all" && (
                    <span className="px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-sm">
                      {selectedChapter}
                    </span>
                  )}
                  {selectedCategory !== "all" && (
                    <span className="px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-sm capitalize">
                      {selectedCategory}
                    </span>
                  )}
                  {selectedYear !== "all" && (
                    <span className="px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-sm">
                      {selectedYear}
                    </span>
                  )}
                </div>
                <button
                  onClick={clearFilters}
                  className="text-sm text-red-600 hover:text-red-700 font-medium flex items-center gap-1"
                >
                  <X className="w-4 h-4" />
                  Clear all
                </button>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Error State */}
        {albumsError && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-6 mb-8">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center flex-shrink-0">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-red-800">Error loading albums</h3>
                <p className="text-red-600 mt-1">
                  {(albumsErrorData as any)?.message || "Failed to load albums. Please try again."}
                </p>
                <button
                  onClick={() => refetchAlbums()}
                  className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
                >
                  Try again
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Featured Albums Section */}
        {featuredAlbums.length > 0 && !hasActiveFilters && (
          <section className="mb-16">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <Star className="w-6 h-6 text-secondary-500" />
                  Featured Collections
                </h2>
                <p className="text-gray-600 mt-1">Handpicked highlights from our community</p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {featuredAlbums.map((album, idx) => (
                <AlbumCard key={album._id} album={album} index={idx} />
              ))}
            </div>
          </section>
        )}

        {/* All Albums Section */}
        <section>
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                {hasActiveFilters ? "Filtered Results" : "All Albums"}
              </h2>
              <p className="text-gray-600 mt-1">
                {filteredAlbums.length} album{filteredAlbums.length !== 1 ? "s" : ""} found
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setViewMode("grid")}
                className={`p-2 rounded-lg transition-colors ${
                  viewMode === "grid"
                    ? "bg-primary-100 text-primary-700"
                    : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                }`}
              >
                <Grid3X3 className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewMode("masonry")}
                className={`p-2 rounded-lg transition-colors ${
                  viewMode === "masonry"
                    ? "bg-primary-100 text-primary-700"
                    : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                }`}
              >
                <LayoutGrid className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Loading State */}
          {albumsLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(6)].map((_, i) => (
                <AlbumSkeleton key={i} />
              ))}
            </div>
          ) : filteredAlbums.length === 0 ? (
            /* Empty State */
            <div className="text-center py-20">
              <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gray-100 flex items-center justify-center">
                <Images className="w-12 h-12 text-gray-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No albums found</h3>
              <p className="text-gray-600 max-w-md mx-auto mb-6">
                {albums.length === 0
                  ? "No albums have been created yet. Check back soon for new memories!"
                  : "Try adjusting your search or filter criteria to find what you're looking for."}
              </p>
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="px-6 py-3 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors font-medium"
                >
                  Clear all filters
                </button>
              )}
            </div>
          ) : (
            /* Albums Grid */
            <div className={`grid gap-8 ${
              viewMode === "grid"
                ? "grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
                : "grid-cols-1 md:grid-cols-2 lg:grid-cols-4"
            }`}>
              {filteredAlbums.map((album, idx) => (
                <AlbumCard key={album._id} album={album} index={idx} />
              ))}
            </div>
          )}
        </section>
      </main>

      {/* Bottom CTA */}
      <section className="bg-gradient-to-r from-primary-700 to-primary-900 py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Want to Contribute?
          </h2>
          <p className="text-white/80 text-lg mb-8">
            Are you a chapter admin? Upload your chapter's memorable moments to share with the community.
          </p>
          <Link
            href="/chapter/gallery"
            className="inline-flex items-center gap-2 px-8 py-4 bg-secondary-500 text-white rounded-xl font-semibold hover:bg-secondary-600 transition-colors shadow-lg"
          >
            <Camera className="w-5 h-5" />
            Upload Photos
          </Link>
        </div>
      </section>
    </div>
  );

  // Album Detail View
  const AlbumDetailView = () => {
    if (albumLoading) {
      return (
        <div className="min-h-screen bg-gradient-to-b from-accent-100 to-white flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-16 h-16 animate-spin text-primary-600 mx-auto mb-4" />
            <p className="text-gray-600 text-lg">Loading album...</p>
          </div>
        </div>
      );
    }

    if (albumError || !selectedAlbum) {
      return (
        <div className="min-h-screen bg-gradient-to-b from-accent-100 to-white flex items-center justify-center p-4">
          <div className="text-center max-w-md">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-100 flex items-center justify-center">
              <AlertCircle className="w-10 h-10 text-red-500" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Album Not Found</h2>
            <p className="text-gray-600 mb-8">
              The album you're looking for doesn't exist or has been removed.
            </p>
            <button
              onClick={() => {
                setSelectedAlbumId(null);
                setCurrentView("gallery");
              }}
              className="px-8 py-3 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors font-medium"
            >
              Back to Gallery
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-gradient-to-b from-accent-100 to-white">
        {/* Album Header */}
        <header className="relative bg-gradient-to-br from-primary-800 via-primary-700 to-primary-900 py-12 lg:py-16">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }} />
          </div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Back Button */}
            <button
              onClick={() => {
                setSelectedAlbumId(null);
                setCurrentView("gallery");
              }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm text-white rounded-xl hover:bg-white/20 transition-colors mb-8"
            >
              <ArrowLeft className="w-5 h-5" />
              Back to Gallery
            </button>

            <div className="lg:flex lg:items-end lg:justify-between">
              <div className="flex-1">
                {/* Badges */}
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="px-3 py-1.5 bg-white/20 backdrop-blur-sm text-white rounded-full text-sm font-medium">
                    {selectedAlbum.chapter?.chapterName || "CONCES"}
                  </span>
                  <span className="px-3 py-1.5 bg-secondary-500/80 text-white rounded-full text-sm font-medium capitalize">
                    {selectedAlbum.category}
                  </span>
                  {selectedAlbum.featured && (
                    <span className="px-3 py-1.5 bg-secondary-500 text-white rounded-full text-sm font-medium flex items-center gap-1">
                      <Star className="w-3 h-3 fill-current" />
                      Featured
                    </span>
                  )}
                </div>

                <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
                  {selectedAlbum.title}
                </h1>
                <p className="text-white/80 text-lg max-w-2xl mb-6">
                  {selectedAlbum.description}
                </p>

                {/* Meta Info */}
                <div className="flex flex-wrap items-center gap-6 text-white/70">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-5 h-5" />
                    {new Date(selectedAlbum.date).toLocaleDateString("en-US", {
                      weekday: "long",
                      month: "long",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </div>
                  <div className="flex items-center gap-2">
                    <Images className="w-5 h-5" />
                    {selectedAlbum.images.length} photos
                  </div>
                </div>

                {/* Tags */}
                {selectedAlbum.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-6">
                    {selectedAlbum.tags.map((tag, idx) => (
                      <span
                        key={idx}
                        className="px-3 py-1 bg-white/10 backdrop-blur-sm text-white rounded-full text-sm"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Images Grid */}
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {selectedAlbum.images.map((image, index) => (
              <div
                key={index}
                className="group relative aspect-square overflow-hidden rounded-xl cursor-pointer shadow-md hover:shadow-xl transition-all duration-300"
                onClick={() => openImageViewer(selectedAlbum, index)}
              >
                <img
                  src={image}
                  alt={`${selectedAlbum.title} - Photo ${index + 1}`}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                
                {/* Hover Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-primary-900/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-4 left-4 right-4">
                    <div className="flex items-center justify-between text-white">
                      <span className="text-sm font-medium">
                        {index + 1} / {selectedAlbum.images.length}
                      </span>
                      <div className="flex items-center gap-2">
                        <ZoomIn className="w-5 h-5" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Number Badge */}
                <div className="absolute top-3 left-3 w-8 h-8 rounded-lg bg-black/50 backdrop-blur-sm flex items-center justify-center text-white text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  {index + 1}
                </div>
              </div>
            ))}
          </div>
        </main>
      </div>
    );
  };

  // Image Viewer Modal
  const ImageViewerModal = () => {
    if (!selectedImage || !selectedAlbum) return null;

    return (
      <div className="fixed inset-0 z-50 bg-black">
        {/* Header */}
        <div className="absolute top-0 left-0 right-0 z-20 bg-gradient-to-b from-black/80 to-transparent p-4">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="text-white">
              <h3 className="font-semibold">{selectedAlbum.title}</h3>
              <p className="text-white/70 text-sm">
                {currentImageIndex + 1} of {selectedAlbum.images.length}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <a
                href={selectedImage}
                download
                target="_blank"
                rel="noopener noreferrer"
                className="p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
              >
                <Download className="w-5 h-5" />
              </a>
              <button
                onClick={closeImageViewer}
                className="p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Navigation Buttons */}
        <button
          onClick={() => navigateImage("prev")}
          className="absolute left-4 top-1/2 -translate-y-1/2 z-20 p-4 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
        >
          <ChevronLeft className="w-8 h-8" />
        </button>
        <button
          onClick={() => navigateImage("next")}
          className="absolute right-4 top-1/2 -translate-y-1/2 z-20 p-4 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
        >
          <ChevronRight className="w-8 h-8" />
        </button>

        {/* Main Image */}
        <div className="absolute inset-0 flex items-center justify-center p-16">
          <img
            src={selectedImage}
            alt={`${selectedAlbum.title} - Photo ${currentImageIndex + 1}`}
            className="max-w-full max-h-full object-contain"
          />
        </div>

        {/* Thumbnails */}
        <div className="absolute bottom-0 left-0 right-0 z-20 bg-gradient-to-t from-black/80 to-transparent p-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex justify-center gap-2 overflow-x-auto pb-2">
              {selectedAlbum.images.map((img, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    setCurrentImageIndex(idx);
                    setSelectedImage(img);
                  }}
                  className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden transition-all duration-300 ${
                    idx === currentImageIndex
                      ? "ring-2 ring-white ring-offset-2 ring-offset-black scale-110"
                      : "opacity-50 hover:opacity-75"
                  }`}
                >
                  <img
                    src={img}
                    alt={`Thumbnail ${idx + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {currentView === "gallery" ? <GalleryView /> : <AlbumDetailView />}
      <ImageViewerModal />
    </>
  );
};

export default GalleryPage;
